import { useState } from 'react'
import { slugify } from '@/lib/utils'
import api from '@/lib/axios'
import type { Tag } from '@/types/admin'

export function useTagsIndexVM(initialTags: Tag[]) {
  const [tags, setTags] = useState<Tag[]>(initialTags)
  const [newName, setNewName] = useState('')
  const [newSlug, setNewSlug] = useState('')
  const [creating, setCreating] = useState(false)
  const [createError, setCreateError] = useState('')

  const [editId, setEditId] = useState<number | null>(null)
  const [editName, setEditName] = useState('')
  const [editSlug, setEditSlug] = useState('')
  const [saving, setSaving] = useState(false)

  const [deleteId, setDeleteId] = useState<number | null>(null)
  const [deleting, setDeleting] = useState(false)

  const handleNewNameChange = (val: string) => {
    setNewName(val)
    setNewSlug(slugify(val))
  }

  const handleCreate = async () => {
    if (!newName.trim()) return
    setCreating(true)
    setCreateError('')
    try {
      const { data } = await api.post('/admin/tags', { name: newName, slug: newSlug })
      if (data.success) {
        setTags((prev) => [...prev, data.tag])
        setNewName('')
        setNewSlug('')
      }
    } catch (err: unknown) {
      if (err && typeof err === 'object' && 'response' in err) {
        const response = (err as { response?: { data?: { errors?: string[] } } }).response
        setCreateError(response?.data?.errors?.join(', ') ?? 'Failed to create tag')
      }
    } finally {
      setCreating(false)
    }
  }

  const startEdit = (tag: Tag) => {
    setEditId(tag.id)
    setEditName(tag.name)
    setEditSlug(tag.slug)
  }

  const cancelEdit = () => {
    setEditId(null)
    setEditName('')
    setEditSlug('')
  }

  const handleUpdate = async () => {
    if (!editId) return
    setSaving(true)
    try {
      const { data } = await api.patch(`/admin/tags/${editId}`, { name: editName, slug: editSlug })
      if (data.success) {
        setTags((prev) => prev.map((t) => (t.id === editId ? data.tag : t)))
        cancelEdit()
      }
    } finally {
      setSaving(false)
    }
  }

  const handleDelete = async () => {
    if (!deleteId) return
    setDeleting(true)
    try {
      await api.delete(`/admin/tags/${deleteId}`)
      setTags((prev) => prev.filter((t) => t.id !== deleteId))
      setDeleteId(null)
    } finally {
      setDeleting(false)
    }
  }

  const isEditing = (tagId: number) => editId === tagId
  const deleteDialogOpen = deleteId !== null
  const createDisabled = !newName.trim()

  return {
    tags,
    newName,
    newSlug,
    creating,
    createError,
    editName,
    editSlug,
    saving,
    deleting,
    deleteDialogOpen,
    createDisabled,
    handleNewNameChange,
    setNewSlug,
    handleCreate,
    startEdit,
    cancelEdit,
    handleUpdate,
    handleDelete,
    setDeleteId,
    isEditing,
    setEditName,
    setEditSlug,
  }
}
